import { useCallback, useEffect, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { ProgressSlider } from '@mesh.ai/rabbit';
import { useSelector } from 'react-redux';
import { selectOrgData } from 'selectors/orgData';
import { themeColors } from 'themes/clients/baseTheme';

import { CLOSED_STATUS_COLOR_MAP, INPROGRESS_STATUS_COLOR_MAP } from 'constants/ProjectConstants';
import { getOrgGoalMeasurementTypes } from 'constants/helper';
import { minifyNumber } from 'constants/numberMinifyHelper';

import InputLabel from 'uiToolkitV1/InputLabel';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

export const OKRProgressSlider = ({
  startValue = 0,
  achievedValue = 0,
  targetValue = 100,
  completionPercentage = 0,
  measure = 'PERCENTAGE',
  goalProgressLevel,
  onChange,
  isReadOnly,
  goalClosureStatus,
}) => {
  const orgData = useSelector(selectOrgData);
  const kRMeasurementTypes = getOrgGoalMeasurementTypes(orgData?.currency ?? null);
  const selectedMeasure = kRMeasurementTypes.find(({ code }) => code === measure);
  const [localAchievedValue, setLocalAchievedValue] = useState(achievedValue);
  /* we are intentionally keeping completion percentage separate from achieved */
  const [localCompletionPercentage, setLocalCompletionPercentage] = useState(completionPercentage);

  const handleOnChangeEnd = useCallback(
    (value) => {
      setLocalAchievedValue(value);

      if (value !== achievedValue) {
        onChange(value);
      }
    },
    [achievedValue]
  );

  useEffect(() => {
    setLocalAchievedValue(achievedValue);
  }, [achievedValue]);

  useEffect(() => {
    setLocalCompletionPercentage(completionPercentage);
  }, [completionPercentage]);

  const achievedValueToDisplay = isReadOnly ? completionPercentage : localAchievedValue;
  const percentageValueToDisplay = isReadOnly ? completionPercentage : localCompletionPercentage;

  const statusColor = goalClosureStatus
    ? CLOSED_STATUS_COLOR_MAP[goalClosureStatus]
    : INPROGRESS_STATUS_COLOR_MAP[goalProgressLevel];

  return (
    <Box>
      <InputLabel labelText="Progress">
        <Box gap="2px">
          <Flex>
            <Box display="flex" flexDirection="column" flex={1} pointerEvents={isReadOnly ? 'none' : 'all'}>
              <ProgressSlider
                isReadOnly={isReadOnly}
                startValue={isReadOnly ? 0 : startValue}
                achievedValue={achievedValueToDisplay}
                targetValue={isReadOnly ? 100 : targetValue}
                onChangeEnd={handleOnChangeEnd}
                status={goalClosureStatus ? `${goalProgressLevel}_${goalClosureStatus}` : goalProgressLevel}
              />
              <Flex justifyContent="space-between" py="4px">
                <TextLabel fontWeight="600" color={themeColors.text.g2}>
                  {startValue + selectedMeasure.symbol}
                </TextLabel>
                <TextLabel fontWeight="600" color={themeColors.text.g2}>
                  {targetValue + selectedMeasure.symbol}
                </TextLabel>
              </Flex>
            </Box>

            <Flex alignItems="center" flex="auto 0 0" paddingLeft={2} height="28px">
              <TextBodyMedium bold color={statusColor?.textColor}>{`${minifyNumber(
                Number(percentageValueToDisplay)
              )}%`}</TextBodyMedium>
            </Flex>
          </Flex>
        </Box>
      </InputLabel>
    </Box>
  );
};
